import {
  AtMaturityPeriod,
  PERIODIC_PERIOD_VALUES,
  type LoanSchedulePeriod,
  type PeriodFrequency,
} from '@src/types/OTC/loanSchedulePeriod';
import { LoanPaymentTypeEnum, LoanQuoteTerminationTypeEnum } from '@talos/kyoko';

export const DEFAULT_PERIODIC_PERIOD: PeriodFrequency = '1M';

export type RememberedPeriodicPeriods = {
  paymentPeriod: PeriodFrequency;
  accrualPeriod: LoanSchedulePeriod;
};

export const DEFAULT_REMEMBERED_PERIODIC_PERIODS: RememberedPeriodicPeriods = {
  paymentPeriod: DEFAULT_PERIODIC_PERIOD,
  accrualPeriod: DEFAULT_PERIODIC_PERIOD,
};

export function isPeriodicPeriod(value: LoanSchedulePeriod | undefined): value is PeriodFrequency {
  return PERIODIC_PERIOD_VALUES.some(period => period === value);
}

export function isAtMaturityPeriod(value: LoanSchedulePeriod | undefined): value is AtMaturityPeriod {
  return value === AtMaturityPeriod;
}

export function isLoanSchedulePeriod(value: LoanSchedulePeriod | undefined): value is LoanSchedulePeriod {
  return isPeriodicPeriod(value) || isAtMaturityPeriod(value);
}

export function isBulletLoanPaymentType(loanPaymentType: LoanPaymentTypeEnum | undefined): boolean {
  return loanPaymentType === LoanPaymentTypeEnum.Bullet;
}

/** Daily < Weekly < Monthly < At Maturity — used for Accrual ≤ Payment. */
const LOAN_SCHEDULE_PERIOD_RANK = {
  '1D': 0,
  '1W': 1,
  '1M': 2,
  [AtMaturityPeriod]: 3,
} as const satisfies Record<LoanSchedulePeriod, number>;

export function getLoanSchedulePeriodRank(period: LoanSchedulePeriod): number {
  return LOAN_SCHEDULE_PERIOD_RANK[period];
}

/**
 * Periodic Accrual options longer than Payment are disabled.
 * Accrual At Maturity is never allowed for Periodic (Bullet locks Accrual to At Maturity separately).
 */
export function isAccrualPeriodAllowedForPaymentPeriod(
  accrualPeriod: LoanSchedulePeriod,
  paymentPeriod: LoanSchedulePeriod | undefined
): boolean {
  if (isAtMaturityPeriod(accrualPeriod) || !isPeriodicPeriod(paymentPeriod)) {
    return false;
  }
  return getLoanSchedulePeriodRank(accrualPeriod) <= getLoanSchedulePeriodRank(paymentPeriod);
}

/** Clamp Accrual down to Payment when Accrual would be longer (Periodic only). */
export function clampAccrualPeriodToPaymentPeriod(
  accrualPeriod: LoanSchedulePeriod | undefined,
  paymentPeriod: PeriodFrequency
): PeriodFrequency {
  if (
    isPeriodicPeriod(accrualPeriod) &&
    getLoanSchedulePeriodRank(accrualPeriod) <= getLoanSchedulePeriodRank(paymentPeriod)
  ) {
    return accrualPeriod;
  }
  return paymentPeriod;
}

export function appendLoanInstrumentBulletSuffix(
  instrument: string,
  loanPaymentType: LoanPaymentTypeEnum | undefined
): string {
  if (isBulletLoanPaymentType(loanPaymentType)) {
    return `${instrument}-BULLET`;
  }
  return instrument;
}

export function applyLoanPaymentTypeChange(params: {
  nextPaymentType: LoanPaymentTypeEnum;
  currentPaymentPeriod?: LoanSchedulePeriod;
  currentAccrualPeriod?: LoanSchedulePeriod;
  rememberedPeriodicPeriods: RememberedPeriodicPeriods;
}): {
  loanPaymentType: LoanPaymentTypeEnum;
  paymentPeriod: LoanSchedulePeriod;
  accrualPeriod: LoanSchedulePeriod;
  rememberedPeriodicPeriods: RememberedPeriodicPeriods;
} {
  switch (params.nextPaymentType) {
    case LoanPaymentTypeEnum.Bullet: {
      const rememberedPaymentPeriod = isPeriodicPeriod(params.currentPaymentPeriod)
        ? params.currentPaymentPeriod
        : params.rememberedPeriodicPeriods.paymentPeriod;
      const rememberedPeriodicPeriods: RememberedPeriodicPeriods = {
        paymentPeriod: rememberedPaymentPeriod,
        // Remember a Periodic-valid accrual (clamp At Maturity / longer periods down to payment).
        accrualPeriod: clampAccrualPeriodToPaymentPeriod(params.currentAccrualPeriod, rememberedPaymentPeriod),
      };
      return {
        loanPaymentType: LoanPaymentTypeEnum.Bullet,
        paymentPeriod: AtMaturityPeriod,
        accrualPeriod: AtMaturityPeriod,
        rememberedPeriodicPeriods,
      };
    }
    case LoanPaymentTypeEnum.Periodic: {
      const paymentPeriod = params.rememberedPeriodicPeriods.paymentPeriod;
      return {
        loanPaymentType: LoanPaymentTypeEnum.Periodic,
        paymentPeriod,
        accrualPeriod: clampAccrualPeriodToPaymentPeriod(params.rememberedPeriodicPeriods.accrualPeriod, paymentPeriod),
        rememberedPeriodicPeriods: params.rememberedPeriodicPeriods,
      };
    }
    default: {
      const _exhaustive: never = params.nextPaymentType;
      return _exhaustive;
    }
  }
}

export type LoanScheduleDurationContext = {
  termType: LoanQuoteTerminationTypeEnum;
  term: string | null | undefined;
  initialTerm: string | null | undefined;
  loanPaymentType: LoanPaymentTypeEnum | undefined;
};

function resolveLoanMaturityDuration(context: LoanScheduleDurationContext): string | undefined {
  if (context.termType === LoanQuoteTerminationTypeEnum.Fixed) {
    return context.term ?? undefined;
  }
  return context.initialTerm ?? undefined;
}

/** Bullet and At Maturity form state resolve to the loan maturity duration on POST. */
export function resolveLoanScheduleDuration(
  period: LoanSchedulePeriod | undefined,
  context: LoanScheduleDurationContext
): string | undefined {
  if (isBulletLoanPaymentType(context.loanPaymentType) || isAtMaturityPeriod(period)) {
    return resolveLoanMaturityDuration(context);
  }
  return period;
}
